import React from "react";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { SKIP } from "./mappingHelpers";

const CREATE_NEW = "";

/**
 * Preview panel listing records a CSV import would create (categories, brands,
 * manufacturers, resellers, ...). When `onChoose` is given, each new name gets
 * a dropdown: create it, use an existing record (`options`), or, with
 * `skipLabel`, leave the products without one (the API receives null).
 * Choices already made are listed with an undo. Without `onChoose` it is a
 * read-only list.
 *
 * Renders nothing when there is nothing new and nothing chosen.
 *
 * @param {string} title        e.g. "New categories"
 * @param {string} noun         singular, lowercase, e.g. "category"
 * @param {string} intro        explanation shown above the new items
 * @param {{name: string, row_count: number, detail?: string}[]} items
 * @param {{name: string, id: number|null, label: string, skipped: boolean}[]} mappings  see describeMappings
 * @param {{id: number, label: string}[]} [options]  existing records to choose from
 * @param {string} [skipLabel]  e.g. "Leave without a brand"
 * @param {boolean} disabled    true while a dry run is in flight
 * @param {(name: string, id: number|string) => void} onChoose  "" = create new, SKIP = leave without
 */
export default function NewItemsPanel({
  title,
  noun,
  intro,
  items = [],
  mappings = [],
  options,
  skipLabel,
  disabled = false,
  onChoose,
}) {
  if (items.length === 0 && mappings.length === 0) return null;

  const hasOptions = Array.isArray(options) && options.length > 0;
  const canChoose =
    typeof onChoose === "function" && (hasOptions || Boolean(skipLabel));

  return (
    <Alert severity="info" variant="outlined" sx={{ mb: 2 }}>
      <AlertTitle sx={{ fontSize: "0.85rem", fontWeight: 600 }}>
        {title}
      </AlertTitle>

      {items.length > 0 && intro && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {intro}
        </Typography>
      )}

      {items.map((item) => (
        <Box
          key={`${item.name}|${item.detail ?? ""}`}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            flexWrap: "wrap",
            mb: 1,
          }}
        >
          <Box sx={{ minWidth: 160 }}>
            <Typography variant="body2" fontWeight={600}>
              {item.name}{" "}
              <Chip
                label="new"
                size="small"
                color="warning"
                variant="outlined"
              />
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {item.detail ? `${item.detail} · ` : ""}used by {item.row_count}{" "}
              {item.row_count === 1 ? "row" : "rows"}
            </Typography>
          </Box>
          {canChoose && (
            <TextField
              select
              size="small"
              value={CREATE_NEW}
              disabled={disabled}
              onChange={(e) => onChoose(item.name, e.target.value)}
              sx={{ minWidth: 240 }}
              SelectProps={{ displayEmpty: true }}
              inputProps={{
                "aria-label": `What to do with ${noun} ${item.name}`,
              }}
            >
              <MenuItem value={CREATE_NEW}>Create new {noun}</MenuItem>
              {skipLabel && <MenuItem value={SKIP}>{skipLabel}</MenuItem>}
              {hasOptions &&
                options.map((option) => (
                  <MenuItem key={option.id} value={option.id}>
                    Use existing: {option.label}
                  </MenuItem>
                ))}
            </TextField>
          )}
        </Box>
      ))}

      {canChoose &&
        mappings.map((mapping) => (
          <Box
            key={mapping.name}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              flexWrap: "wrap",
              mb: 0.5,
            }}
          >
            <Typography variant="body2">
              {mapping.skipped ? (
                <>
                  Products with {noun} <strong>{mapping.name}</strong> will be{" "}
                  {mapping.label}
                </>
              ) : (
                <>
                  <strong>{mapping.name}</strong> will use your existing {noun}{" "}
                  <strong>{mapping.label}</strong>
                </>
              )}
            </Typography>
            <Button
              size="small"
              disabled={disabled}
              onClick={() => onChoose(mapping.name, CREATE_NEW)}
              sx={{ textTransform: "none" }}
            >
              Create new instead
            </Button>
          </Box>
        ))}
    </Alert>
  );
}
